/**
 * Unified API Module
 *
 * This module provides a configurable API class that can be used for both
 * server-side and client-side API calls with customizable override functions.
 *
 * Usage:
 * 1. Server-side: const api = new Api()
 * 2. Client-side: const api = new Api({ store, actions })
 * 3. Custom override: const api = new Api({ overrideFunction: myCustomOverride })
 */

import { Store } from 'redux';

import * as admin from './admin';
import * as assetImage from './asset-image';
import * as assetMaster from './asset-master';
import * as auth from './auth';
import * as combined from './combined';
import * as datasets_ls from './datasets_ls';
import * as dates from './dates';
import * as docs from './docs';
import * as dslSignal from './dsl_signal';
import * as embedWidgets from './embed-widgets';
import * as feedback from './feedback';
import * as jobLogs from './job-logs';
import * as job from './jobs';
import * as loadData from './load-data';
import * as localApi from './local-api';
import * as orgPublish from './org-publish';
import * as poll from './poll';
import * as s3 from './s3';
import apis from './scrud';
import * as uiApi from './ui-api';
import * as user_api from './user-api';
import * as user from './users';
import clientOverrideFunction from './utils/client-override';
import * as videos from './videos';
import { deepmerge } from '../libs';

const getDefaultApiConfig = () => ({
  endpoint: '/api/v1/' as string,
  userapiendpoint: '/api/' as string,
  embeddedWidgets: 'https://embedded.finsera.com' as string,
  headers: {},
  returnHeaders: false,
});

export type ApiConfig = ReturnType<typeof getDefaultApiConfig>;

export interface ApiInstanceConfig {
  store?: Store;
  actions?: any;
  overrideFunction?: ((_store?: Store, _actions?: any) => (_func: any) => any) | null;
  config?: Partial<ApiConfig>;
}

// Utility type to get the return type of higher-order functions that take config as first parameter
type ConfigBoundFunction<T> = T extends (_config: ApiConfig) => infer R ? R : T;

// Utility type to transform all functions in a module by getting their return types (after config binding)
export type ConfigBoundModule<T> = {
  [K in keyof T]: T[K] extends (_config: ApiConfig) => any
    ? ConfigBoundFunction<T[K]>
    : T[K] extends object
      ? ConfigBoundModule<T[K]>
      : T[K];
};

// Automatically generate types for all SCRUD APIs
type ScrudApiTypes = {
  [K in keyof typeof apis]: ConfigBoundModule<(typeof apis)[K]>;
};

class ApiBase {
  private store?: Store;
  private actions?: any;
  private overrideFunction: ((_store?: Store, _actions?: any) => (_func: any) => any) | null;
  private config: ApiConfig;

  // API modules - config is already bound, so remove config parameter from function signatures
  public readonly admin: ConfigBoundModule<typeof admin>;
  public readonly auth: ConfigBoundModule<typeof auth>;
  public readonly combined: ConfigBoundModule<typeof combined>;
  public readonly datasets_ls: ConfigBoundModule<typeof datasets_ls>;
  public readonly dates: ConfigBoundModule<typeof dates>;
  public readonly docs: ConfigBoundModule<typeof docs>;
  public readonly feedback: ConfigBoundModule<typeof feedback>;
  public readonly assetImage: ConfigBoundModule<typeof assetImage>;
  public readonly assetMaster: ConfigBoundModule<typeof assetMaster>;
  public readonly jobLogs: ConfigBoundModule<typeof jobLogs>;
  public readonly jobs: ConfigBoundModule<typeof job> & ConfigBoundModule<typeof apis.jobs>;
  public readonly loadData: ConfigBoundModule<typeof loadData>;
  public readonly localApi: ConfigBoundModule<typeof localApi>;
  public readonly poll: ConfigBoundModule<typeof poll>;
  public readonly s3: ConfigBoundModule<typeof s3>;
  public readonly uiApi: ConfigBoundModule<typeof uiApi>;
  public readonly user_api: ConfigBoundModule<typeof user_api>;
  public readonly users: ConfigBoundModule<typeof user> & ConfigBoundModule<typeof apis.users>;
  public readonly videos: ConfigBoundModule<typeof videos>;
  public readonly orgPublish: ConfigBoundModule<typeof orgPublish>;
  public readonly embedWidgets: ConfigBoundModule<typeof embedWidgets>;
  public readonly dslSignal: ConfigBoundModule<typeof dslSignal>;

  // SCRUD APIs are automatically typed via ScrudApiTypes interface implementation

  constructor({ store, actions, overrideFunction = null, config }: ApiInstanceConfig = {}) {
    this.store = store;
    this.actions = actions;
    this.overrideFunction = overrideFunction;
    // Each instance gets its own independent config, not tied to global config
    this.config = config ? { ...getDefaultApiConfig(), ...config } : getDefaultApiConfig();

    // Create the base API structure with config injected to all modules
    const baseApis = deepmerge(this.bindConfigToModule(apis, 'scrud'), {
      admin: this.bindConfigToModule(admin, 'admin'),
      auth: this.bindConfigToModule(auth, 'auth'),
      combined: this.bindConfigToModule(combined, 'combined'),
      datasets_ls: this.bindConfigToModule(datasets_ls, 'datasets_ls'),
      dates: this.bindConfigToModule(dates, 'dates'),
      docs: this.bindConfigToModule(docs, 'docs'),
      feedback: this.bindConfigToModule(feedback, 'feedback'),
      assetImage: this.bindConfigToModule(assetImage, 'assetImage'),
      assetMaster: this.bindConfigToModule(assetMaster, 'assetMaster'),
      jobLogs: this.bindConfigToModule(jobLogs, 'jobLogs'),
      jobs: { ...this.bindConfigToModule(job, 'jobs'), ...this.bindConfigToModule(apis.jobs, 'scrud.jobs') },
      loadData: this.bindConfigToModule(loadData, 'loadData'),
      localApi: this.bindConfigToModule(localApi, 'localApi'),
      poll: this.bindConfigToModule(poll, 'poll'),
      s3: this.bindConfigToModule(s3, 's3'),
      uiApi: this.bindConfigToModule(uiApi, 'uiApi'),
      user_api: this.bindConfigToModule(user_api, 'user_api'),
      users: { ...this.bindConfigToModule(user, 'users'), ...this.bindConfigToModule(apis.users, 'scrud.users') },
      videos: this.bindConfigToModule(videos, 'videos'),
      orgPublish: this.bindConfigToModule(orgPublish, 'orgPublish'),
      embedWidgets: this.bindConfigToModule(embedWidgets, 'embedWidgets'),
      dslSignal: this.bindConfigToModule(dslSignal, 'dslSignal'),
    });

    // Apply overrides if override function is provided
    if (this.overrideFunction) {
      const overrideCall = this.overrideFunction(this.store, this.actions);
      this.applyOverrides(baseApis, overrideCall);
    } else {
      // If no override function, use APIs directly
      Object.assign(this, baseApis);
    }
  }

  /**
   * Bind config to all functions in a module
   */
  private bindConfigToModule(module: any, moduleName?: string): any {
    if (!module || typeof module !== 'object') {
      if (moduleName) {
        console.warn(`API Module '${moduleName}' is not properly defined: ${typeof module}`);
      }
      return module;
    }

    const boundModule: any = {};
    const errors: string[] = [];

    Object.keys(module).forEach((key) => {
      const value = module[key];

      if (typeof value === 'function') {
        try {
          // Check if this is a higher-order function that returns a function (like admin functions)
          // These functions take config as first parameter and return the actual API function
          const boundFunction = value(this.config);

          // Validate that the bound function is actually a function
          if (typeof boundFunction !== 'function') {
            const moduleRef = moduleName || 'unknown';
            const functionType = typeof boundFunction;
            errors.push(
              `Function '${key}' in module '${moduleRef}' binding failed. Expected function, got: ${functionType}`
            );
            return;
          }

          boundModule[key] = boundFunction;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          errors.push(
            `Failed to bind config to function '${key}' in module '${moduleName || 'unknown'}': ${errorMessage}`
          );
          // Don't add the function if binding failed
        }
      } else if (typeof value === 'object' && value !== null) {
        // Recursively bind config to nested objects
        boundModule[key] = this.bindConfigToModule(value, `${moduleName || 'unknown'}.${key}`);
      } else {
        // Copy primitive values as-is
        boundModule[key] = value;
      }
    });

    // Report any errors found during binding
    if (errors.length > 0) {
      console.error(`API Configuration Errors in module '${moduleName || 'unknown'}':`, errors);
      // Optionally throw an error to stop initialization
      // throw new Error(`API module '${moduleName}' has configuration errors: ${errors.join(', ')}`);
    }

    return boundModule;
  }

  private applyOverrides(apis: any, overrideCall: (_func: any) => any) {
    // First level of the object are the actual api calls
    Object.keys(apis).forEach((key) => {
      const currentApi = apis[key as keyof typeof apis];
      const overriddenApi = { ...currentApi };

      Object.keys(currentApi).forEach((funcKey) => {
        overriddenApi[funcKey] = overrideCall(currentApi[funcKey]);
      });

      (this as any)[key] = overriddenApi;
    });
  }

  /**
   * Update the configuration for this API instance
   */
  public updateConfig(newConfig: Partial<ApiConfig>) {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get the current configuration
   */
  public getConfig(): ApiConfig {
    return { ...this.config };
  }
}

// ApiType is the base API type that includes all the modules
export type ApiType = Api & ScrudApiTypes;

// ApiTypeWithHeaders is the ApiType with headers in the response
// Utility type to transform all functions in a module to include headers in the response
export type ApiTypeWithHeaders = {
  [K in keyof ApiType]: ApiType[K] extends (..._args: any[]) => Promise<infer R>
    ? (..._args: Parameters<ApiType[K]>) => Promise<{ responseData: R; headers: Headers }>
    : ApiType[K] extends (..._args: any[]) => infer R
      ? (..._args: Parameters<ApiType[K]>) => { responseData: R; headers: Headers }
      : ApiType[K] extends object
        ? {
            [P in keyof ApiType[K]]: ApiType[K][P] extends (..._args: any[]) => Promise<infer R>
              ? (..._args: Parameters<ApiType[K][P]>) => Promise<{ responseData: R; headers: Headers }>
              : ApiType[K][P] extends (..._args: any[]) => infer R
                ? (..._args: Parameters<ApiType[K][P]>) => { responseData: R; headers: Headers }
                : ApiType[K][P] extends object
                  ? {
                      [Q in keyof ApiType[K][P]]: ApiType[K][P][Q] extends (..._args: any[]) => Promise<infer R>
                        ? (..._args: Parameters<ApiType[K][P][Q]>) => Promise<{ responseData: R; headers: Headers }>
                        : ApiType[K][P][Q] extends (..._args: any[]) => infer R
                          ? (..._args: Parameters<ApiType[K][P][Q]>) => { responseData: R; headers: Headers }
                          : ApiType[K][P][Q];
                    }
                  : ApiType[K][P];
          }
        : ApiType[K];
};

// Create a default server-side API instance (no overrides)
export const createServerApi = <T = ApiType>(config?: Partial<ApiConfig>) => {
  return new Api({ config }) as T;
};

// Create a client-side API instance with default overrides
export const createClientApi = (store: Store, actions: any, config?: Partial<ApiConfig>) => {
  return new Api({ store, actions, config, overrideFunction: clientOverrideFunction }) as ApiType;
};

// Export the Api class with SCRUD types automatically included via intersection
export class Api extends ApiBase {}

export default Api;
